import {
  HeartHandshake, Droplets, ShoppingBag, Shirt, Phone, Users, Receipt, Sparkles,
  Pill, Dumbbell, UtensilsCrossed, Car, Flower2, Bath, BedDouble, Stethoscope,
} from "lucide-react";

export const TASK_TYPES = [
  { value: "appointment", label: "Client Appointment", color: "bg-sage/15 text-sage-700", dot: "bg-sage", hex: "#8A9A86", icon: HeartHandshake },
  { value: "shower", label: "Shower / Personal Care", color: "bg-terracotta/15 text-terracotta", dot: "bg-terracotta", hex: "#C08A6D", icon: Droplets },
  { value: "shopping", label: "Shopping", color: "bg-amber-100 text-amber-700", dot: "bg-amber-400", hex: "#D9A441", icon: ShoppingBag },
  { value: "laundry", label: "Laundry", color: "bg-blue-100 text-blue-700", dot: "bg-blue-400", hex: "#5B8DD9", icon: Shirt },
  { value: "phone_meeting", label: "Phone Meeting", color: "bg-violet-100 text-violet-700", dot: "bg-violet-400", hex: "#8B7BC7", icon: Phone },
  { value: "in_person_meeting", label: "In-Person Meeting", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", hex: "#3E9E6B", icon: Users },
  { value: "billing", label: "Billing", color: "bg-rose-100 text-rose-700", dot: "bg-rose-400", hex: "#D96A7F", icon: Receipt },
  { value: "other", label: "Other", color: "bg-charcoal/10 text-charcoal-soft", dot: "bg-charcoal/40", hex: "#8A8880", icon: Sparkles },
];

// Extra colors auto-assigned (in order) to categories Nicole adds herself
export const EXTRA_TYPE_COLORS = [
  { color: "bg-pink-100 text-pink-700", dot: "bg-pink-400", hex: "#E0729A" },
  { color: "bg-cyan-100 text-cyan-700", dot: "bg-cyan-500", hex: "#2FA8C9" },
  { color: "bg-lime-100 text-lime-700", dot: "bg-lime-500", hex: "#7CB342" },
  { color: "bg-orange-100 text-orange-700", dot: "bg-orange-400", hex: "#E08A3C" },
  { color: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-400", hex: "#6B7BD6" },
  { color: "bg-teal-100 text-teal-700", dot: "bg-teal-500", hex: "#2FA79A" },
  { color: "bg-fuchsia-100 text-fuchsia-700", dot: "bg-fuchsia-400", hex: "#C25FB8" },
  { color: "bg-sky-100 text-sky-700", dot: "bg-sky-400", hex: "#58A6D8" },
];

export const TYPE_ICONS = {
  sparkles: Sparkles,
  pill: Pill,
  dumbbell: Dumbbell,
  utensils: UtensilsCrossed,
  car: Car,
  flower: Flower2,
  bath: Bath,
  bed: BedDouble,
  stethoscope: Stethoscope,
};

// Pick a fitting icon from the category name (e.g. "Medication Assistance" -> pill)
export const guessTypeIcon = (label) => {
  const l = String(label || "").toLowerCase();
  if (/med|pill|qmap|rx|dose/.test(l)) return "pill";
  if (/exercise|walk|gym|therapy|stretch|pt\b/.test(l)) return "dumbbell";
  if (/meal|cook|food|dinner|lunch|breakfast|prep|snack/.test(l)) return "utensils";
  if (/drive|errand|transport|ride|trip|appointment.*dr|dr.*appointment/.test(l)) return "car";
  if (/garden|plant|flower/.test(l)) return "flower";
  if (/bath|shower|hygiene|wash/.test(l)) return "bath";
  if (/overnight|bed|sleep|respite|night/.test(l)) return "bed";
  if (/doctor|nurse|clinic|health|vital|wound|oxygen|check.?up/.test(l)) return "stethoscope";
  return "sparkles";
};

// Turn a custom category document from the API into the same shape as TASK_TYPES
export const customTypeMeta = (doc) => {
  const c = EXTRA_TYPE_COLORS[(doc.color_idx ?? 0) % EXTRA_TYPE_COLORS.length];
  return {
    value: doc.value,
    label: doc.label,
    color: c.color,
    dot: c.dot,
    hex: c.hex,
    icon: TYPE_ICONS[doc.icon] || Sparkles,
    custom: true,
  };
};

export const UNITS = ["hour", "day", "visit", "item", "flat"];

export const taskMeta = (type) =>
  TASK_TYPES.find((t) => t.value === type) || TASK_TYPES[TASK_TYPES.length - 1];

export const currency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

export const INVOICE_STATUS = {
  draft: { label: "Draft", color: "bg-charcoal/10 text-charcoal-soft" },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700" },
  paid: { label: "Paid", color: "bg-emerald-100 text-emerald-700" },
};

export const PERSONAL_CARE = ["Bathing", "Dressing", "Grooming", "Toileting", "Mobility", "Oral hygiene", "Feeding"];
export const ASSISTED_WITH = ["Exercise", "Oxygen", "Wound care", "Medications", "Supplements"];
export const HOUSEKEEPING = ["Dishes", "Laundry", "Floors", "Trash", "Bathroom", "Bed linens", "Meal prep"];

// Client profile descriptors (care plan snapshot)
export const COGNITIVE_OPTIONS = ["Alert & oriented", "Mild forgetfulness", "Memory loss (dementia / Alzheimer's)", "Varies day to day"];
export const MOBILITY_OPTIONS = ["Independent", "Cane or walker", "Wheelchair", "Bedbound / full assist"];
export const CARE_NEEDS = [
  "Bathing & hygiene", "Dressing", "Meals & nutrition", "Mobility & transfers",
  "Medication assistance", "Companionship", "Housekeeping", "Laundry",
  "Exercise & walks", "Transportation", "Memory support", "Overnight care",
];
